import React, { Fragment } from "react";
import styled from "styled-components";
import { connect } from "formik";
import numeral from "numeral";
import { isEmpty, get } from "lodash";
import { compose } from "redux";
import { Grid } from "@material-ui/core";
import { FormikInputTypeAhead, FormikInputPercent, FormikInputNumber, DSCR_TYPES, FormikInputText } from "@loanbossinc/component-library";

const Divider = styled.div`&& {    
    border: 1px solid #DADCE0;
    width: 100%;
    height: 0px;
    margin-top:30px;
}`;

const StyledGridContainer = styled(Grid)`&&{
    margin-top: 26px
}`;
const StyledFormikInputNumber = styled(FormikInputNumber)`&&&&{
    width: 100%
}`;
const StyledFormikInputPercent = styled(FormikInputPercent)`&&&&{
    width: 100%
}`;
const StyledFormikInputText = styled(FormikInputText)`&&&&{
    width: 100%
}`;
const StyledFormikInputTypeAhead = styled(FormikInputTypeAhead)`
    border-bottom: 1px solid #2196F3;
    width: 100%;
`;
const StyledHeaderContainer = styled(Grid)`&&{
    margin-top: 23px;
}`;
const StyledHeader = styled.span`&&{
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 23px;

    color: #000000;
}`;

const getFixedFloating = values => {
  const rateTypeId = get(values, "details.rateTypeId");
  if (rateTypeId && rateTypeId === "53669856-4681-11e9-82f6-0242ac120002") {
    return 1;
  } else if (rateTypeId && rateTypeId === "5813c493-4681-11e9-82f6-0242ac120002") {
    return 2;
  }

  return 0;
};

const getDSCRTypeId = values => get(values, "covenants.dscrTypeId");

const ActualProjectedCashflows = values => {
  const dscrTypeId = getDSCRTypeId(values);
  if (dscrTypeId !== DSCR_TYPES.ACTUAL_PROJECTED_CASHFLOWS) {
    return null;
  }

  return (
    <StyledGridContainer item xs={12}>
      <StyledFormikInputNumber
        name={"covenants.dscrDebtServiceTerm"}
        label={"Projected Months"}
        variant={"filled"}
      />
    </StyledGridContainer>
  );
};

const ActualTrailingCashflows = values => {
  const dscrTypeId = getDSCRTypeId(values);
  if (dscrTypeId !== DSCR_TYPES.ACTUAL_TRAILING_CASHFLOWS) {
    return null;
  }

  return (
    <StyledGridContainer item xs={12}>
      <StyledFormikInputNumber
        name={"covenants.dscrDebtServiceTerm"}
        label={"Trailing Months"}
        variant={"filled"}
      />
    </StyledGridContainer>
  );
};

const HypotheticalAmortization = values => {
  const dscrTypeId = getDSCRTypeId(values);
  if (dscrTypeId !== DSCR_TYPES.HYPOTHETICAL_AMORTIZATION) {
    return null;
  }

  return (
    <>
      <StyledGridContainer item xs={12}>
        <StyledFormikInputNumber
          name={"covenants.dscrAmortTerm"}
          label={"DSCR Amortization Term (months)"}
          variant={"filled"}
        />
      </StyledGridContainer>

      <StyledGridContainer item xs={12}>
        <StyledFormikInputPercent
          name={"covenants.dscrFixedRate"}
          label={"DSCR Fixed Rate (%)"}
          decimals={3}
          variant={"filled"}
        />
      </StyledGridContainer>

      <StyledGridContainer item xs={12}>
        <StyledFormikInputPercent
          name={"covenants.dscrTreasurySpread"}
          label={"DSCR Treasury Spread (%)"}
          decimals={3}
          variant={"filled"}
        />
      </StyledGridContainer>

      <StyledGridContainer item xs={12}>
        <StyledFormikInputPercent
          name={"covenants.dscrFloatingRateSpread"}
          label={"DSCR Floating Rate Spread (%)"}
          decimals={3}
          variant={"filled"}
        />
      </StyledGridContainer>
    </>
  );
};

const FinancialCovenantsSection = ({ formik }) => {
  const { values } = formik;
  const dscrTypes = values && values.ancillary && values.ancillary.dscrTypes ? values.ancillary.dscrTypes : [];
  const loanTypes = values && values.ancillary && values.ancillary.loanTypes ? values.ancillary.loanTypes : [];
  const defaultIndex = getFixedFloating(values);
  const indexes = values && values.ancillary ? defaultIndex == 1 ? values.ancillary.fixedIndexes : values.ancillary.floatingIndexes : [];

  return (
    <Grid container>
      <StyledHeaderContainer item xs={12}><StyledHeader>Financial Covenants</StyledHeader></StyledHeaderContainer>

      <StyledGridContainer container item xs={12}>
        <Grid item xs={4}>
          <StyledFormikInputPercent
            name={"covenants.dscrStabilized"}
            label={"DSCR Req."}
            decimals={3}
            variant={"filled"}
          />
        </Grid>
        <Grid item xs={1} />
        <Grid item xs={7}>
          <StyledFormikInputTypeAhead
            id="loanTypeId"
            name="covenants.dscrTypeId"
            label={"DSCR Calc Type"}
            options={dscrTypes}
            displayWith={option => option && option.name}
            setValueWith={option => option && option.id}
            onChange={value => {}}
            className="input"
            variant={"filled"}
          />
        </Grid>
      </StyledGridContainer>

      <StyledGridContainer container item xs={12}>
        {ActualProjectedCashflows(values)}
        {ActualTrailingCashflows(values)}
        {HypotheticalAmortization(values)}
      </StyledGridContainer>

      <Divider />

      <StyledGridContainer container item xs={12}>
        <StyledFormikInputText
          name={"covenants.dscrCovenants"}
          label={"DSCR Covenants"}
          variant={"filled"}
          multiline
        />
      </StyledGridContainer>

      <StyledGridContainer container item xs={12}>
        <StyledFormikInputPercent
          name={"covenants.debtYieldStabilized"}
          label={"Debt Yield Req. (%)"}
          decimals={3}
          variant={"filled"}
        />
      </StyledGridContainer>

      <StyledGridContainer container item xs={12}>
        <StyledFormikInputText
          name={"covenants.debtYieldCovenants"}
          label={"Debt Yield Covenants"}
          variant={"filled"}
          multiline
        />
      </StyledGridContainer>

      <StyledGridContainer container item xs={12}>
        <StyledFormikInputText
          name={"covenants.financialCovenantsMisc"}
          label={"Miscellaneous"}
          variant={"filled"}
          multiline
        />
      </StyledGridContainer>
      <Divider />
    </Grid>
  );
};

export default compose(connect)(FinancialCovenantsSection);
